/** @jsx jsx */
import React, { forwardRef, type Ref } from 'react';
import { cssMap, styled, jsx } from '@compiled/react';
import { type SizeType, BORDER_WIDTH, AVATAR_SIZES } from '@atlaskit/avatar';

import { token } from '@atlaskit/tokens';

import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { Z_INDEX } from './constants.tsx';

export const hiddenCheckboxStyles = {
	position: 'absolute',
	width: '100%',
	height: '100%',
	padding: 0,
	border: 0,
	opacity: 0,
	overflow: 'hidden',
	'white-space': 'nowrap',
	cursor: 'pointer',
};

type AvatarWrapperProps = {
	selected?: boolean;
	zIndex: number;
	jiggle?: boolean;
	ref?: Ref<HTMLDivElement>;
	isDisabled?: boolean;
};

export const AvatarWrapperNew = forwardRef<HTMLDivElement, AvatarWrapperProps>((props, ref) => {
	return <AvatarWrapperBase {...props} css={wrapperNewStyles.root} ref={ref} />;
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarWrapperBase = styled.div<AvatarWrapperProps>({
	display: 'inline-flex',
	alignItems: 'center',
	height: '40px',
	position: 'relative',
	margin: 0,
	marginLeft: token('space.negative.100'),
	background: 'none',
	verticalAlign: 'top',
	padding: 0,
	transition: 'transform 0.1s ease-out',
	borderRadius: token('radius.full'),
	border: 0,
	outline: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	zIndex: (props: AvatarWrapperProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		props.selected ? props.zIndex + Z_INDEX.SELECTED : props.zIndex,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},
	'&:hover, &:focus, &:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		transform: (props: AvatarWrapperProps) =>
			!props.isDisabled ? `translateY(${props.jiggle ? -4 : 0}px)` : 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		zIndex: (props: AvatarWrapperProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			!props.isDisabled ? props.zIndex + Z_INDEX.HOVER : props.zIndex,
		'&::after': {
			position: 'absolute',
			content: "''",
			width: '100%',
			top: 0,
			left: '-1px',
			right: '-1px',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			bottom: (props: AvatarWrapperProps) => (!props.isDisabled ? `-${props.jiggle ? 4 : 0}px` : 0),
			zIndex: -1,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			transform: (props: AvatarWrapperProps) => (!props.isDisabled ? 'translateY(4px)' : 'none'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
			cursor: ({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer'),
		},
	},
});

export const AvatarWrapper = componentWithCondition(
	isVisualRefreshEnabled,
	AvatarWrapperNew,
	AvatarWrapperBase,
);

const wrapperNewStyles = cssMap({
	root: {
		height: 'unset',
		border: 0,
		padding: 0,
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.full', '50%'),
		background: 'none',
		outline: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionAvatar = styled.button<{ size?: SizeType; isDisabled?: boolean }>({
	margin: 0,
	padding: 0,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.full', '50%'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${BORDER_WIDTH}px`,
	borderStyle: 'solid',
	borderColor: `${token('color.text.inverse')}`,
	backgroundColor: token('color.background.accent.gray.subtlest'),
	transition: 'background-color 0.1s ease-out 0s',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	cursor: ({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer'),
	'&:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		boxShadow: ({ isDisabled }) =>
			isDisabled
				? 'none'
				: `0 0 0 ${token('border.width.focused')} ${token('color.background.neutral.pressed')}`,
		outline: 'none',
	},
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		backgroundColor: ({ isDisabled }) =>
			isDisabled
				? token('color.background.accent.gray.subtlest')
				: token('color.background.accent.gray.subtler'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionAvatarSpacer = styled.span({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.full', '50%'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES.medium}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES.medium}px`,
	backgroundColor: 'transparent',
	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: `${AVATAR_SIZES.medium}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarBorder = styled.div<{ selected?: boolean; isSoftwareFullTheming?: boolean }>({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.full', '50%'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${BORDER_WIDTH}px`,
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderColor: ({ selected, isSoftwareFullTheming }) =>
		// eslint-disable-next-line no-nested-ternary
		selected
			? isSoftwareFullTheming
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					Tokens.COLOR_BORDER_SELECTED
				: token('color.icon.brand')
			: 'transparent',
});
